import { editor, languages } from "monaco-editor";

import { incompleteEntityRegex, runtimeTypes } from "./regex-definitions";

/**
 * Completion handler that splits entity names by dots and provides contextual suggestions
 */
export const splitCompletionHandler: languages.CompletionItemProvider = {
  provideCompletionItems: async (
    model: editor.ITextModel,
    position: { column: number; lineNumber: number },
  ): Promise<languages.CompletionList> => {
    const offset = model.getOffsetAt(position);
    const textUntilPosition = model.getValue().substring(0, offset);

    const match = incompleteEntityRegex.exec(textUntilPosition);
    if (!match) return { suggestions: [] };

    const prefix = match[1];
    const runtimeEntities = runtimeTypes.map((type) => `runtime.${type}`);
    const entityList = window._latestEntityList.concat(runtimeEntities);
    if (!entityList.length) return { suggestions: [] };

    const parts = prefix.split(".");
    const base = parts[0] || "";

    const word = model.getWordUntilPosition(position);
    const range = {
      endColumn: word.endColumn,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      startLineNumber: position.lineNumber,
    };

    let suggestions = [];
    if (parts.length === 1) {
      // Top level suggestions (e.g. "miru", "runtime")
      const topLevelSuggestions = Array.from(
        new Set(entityList.map((e) => e.split(".")[0])),
      );
      suggestions = topLevelSuggestions
        .filter((s) => s.startsWith(base))
        .map((s) => ({
          insertText: s,
          kind: languages.CompletionItemKind.Enum,
          label: s,
          range,
        }));
    } else {
      // Nested suggestions (e.g. "miru.entity", "runtime.string")
      const prefixWithoutLastPart = parts.slice(0, -1).join(".");
      const lastPart = parts[parts.length - 1];

      suggestions = entityList
        .filter((e) => e.startsWith(prefixWithoutLastPart))
        .map((e) => e.substring(prefixWithoutLastPart.length + 1))
        .map((e) => e.split(".")[0])
        .filter((value, index, self) => self.indexOf(value) === index)
        .filter((s) => s.startsWith(lastPart))
        .map((s) => ({
          insertText: s,
          kind: languages.CompletionItemKind.Property,
          label: s,
          range,
        }));
    }

    return { suggestions };
  },
  triggerCharacters: ["<", "."],
};
